/**
 * What the browser remembers between page loads.
 *
 * Two separate things, deliberately kept apart:
 *
 * - The **room session** (room code + display name) is what makes a refresh,
 *   a dropped connection or a dyno restart recoverable. Rooms live only in the
 *   server's memory, so without this a restart would leave everyone stranded
 *   on a screen for a room that no longer exists.
 * - The **host token** is a credential. It sits in `localStorage` because the
 *   socket.io handshake has to send it explicitly, which rules out an
 *   httpOnly cookie. The trade-off is that any script running on this origin
 *   could read it, so the token is deliberately short-lived (12h) and grants
 *   nothing beyond creating and running rooms.
 */

const SESSION_KEY = 'point-ker:session';
const TOKEN_KEY = 'point-ker:token';

export interface RoomSession {
  readonly roomId: string;
  readonly name: string;
  readonly isSpectator: boolean;
}

/**
 * `localStorage` throws in private-mode Safari and when storage is full, and
 * an unreadable session is never worth breaking the app over — the user just
 * gets the join form instead of an automatic rejoin.
 *
 * Exported so every read and write of persisted state goes through the same
 * guard; `jira/settings.ts` is the other caller.
 */
export function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : (JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable: the app still works, it just cannot auto-rejoin.
  }
}

export function loadSession(): RoomSession | null {
  const stored = readJson<Partial<RoomSession>>(SESSION_KEY);
  if (!stored || typeof stored.roomId !== 'string' || typeof stored.name !== 'string') {
    return null;
  }
  return {
    roomId: stored.roomId,
    name: stored.name,
    isSpectator: stored.isSpectator === true,
  };
}

export function saveSession(session: RoomSession): void {
  writeStored(SESSION_KEY, JSON.stringify(session));
}

export function clearSession(): void {
  writeStored(SESSION_KEY, null);
}

export function loadToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function saveToken(token: string): void {
  writeStored(TOKEN_KEY, token);
}

export function clearToken(): void {
  writeStored(TOKEN_KEY, null);
}
